import { createBrowserRouter, Navigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { MainLayout } from '@/components/layout'
import {
  Dashboard,
  Clientes,
  Movimientos,
  Presupuestos,
  Facturas,
  Documentos,
  Cuentas,
  Gastos,
  Servicios,
  Reportes,
  Configuracion,
  Calendario,
  Tareas,
  Login,
  Usuarios,
  Auditoria,
  Impuestos,
  Notificaciones,
  CasilleroElectronico,
} from '@/pages'

import { PresupuestoNuevo } from '@/pages/PresupuestoNuevo'
import { PresupuestoEditar } from '@/pages/PresupuestoEditar'
import { PresupuestoDetalle } from '@/pages/PresupuestoDetalle'
import { Suspense, lazy } from 'react'
import { isCopilotoEnabled } from '@/lib/featureFlags'

// Settings sub-pages (lazy loaded)
const GeneralSettings = lazy(() => import('@/components/configuracion/GeneralSettings').then(m => ({ default: m.GeneralSettings })))
const BrandSettings = lazy(() => import('@/components/configuracion/BrandSettings').then(m => ({ default: m.BrandSettings })))
const FiscalSettings = lazy(() => import('@/components/configuracion/FiscalSettings').then(m => ({ default: m.FiscalSettings })))
const CommunicationSettings = lazy(() => import('@/components/configuracion/CommunicationSettings').then(m => ({ default: m.CommunicationSettings })))
const FacturacionSettings = lazy(() => import('@/components/configuracion/FacturacionSettings').then(m => ({ default: m.FacturacionSettings })))
const SocialSettings = lazy(() => import('@/components/configuracion/SocialSettings').then(m => ({ default: m.SocialSettings })))
const NotificationSettings = lazy(() => import('@/components/configuracion/NotificationSettings').then(m => ({ default: m.NotificationSettings })))
const AccountSettings = lazy(() => import('@/components/configuracion/AccountSettings').then(m => ({ default: m.AccountSettings })))

// Páginas públicas (legales e informativas)
const TerminosServicio = lazy(() => import('@/pages/TerminosServicio'))
const PoliticaPrivacidad = lazy(() => import('@/pages/PoliticaPrivacidad'))
const Soporte = lazy(() => import('@/pages/Soporte'))
const ContratoCliente = lazy(() => import('@/pages/ContratoCliente'))

// Copiloto Contable (ORR-5) - lazy para no cargar el bundle de @ai-sdk si el user no tiene el feature flag
const CopilotoContable = lazy(() => import('@/pages/CopilotoContable'))

// Ayuda y notas de versión: lazy, así el contenido en Markdown no entra en el bundle principal
const Ayuda = lazy(() => import('@/pages/Ayuda'))
const AyudaArticulo = lazy(() => import('@/pages/AyudaArticulo'))
const Novedades = lazy(() => import('@/pages/Novedades'))

// Admin sync de vencimientos (ORR-100) - lazy
const SyncVencimientos = lazy(() => import('@/pages/SyncVencimientos'))

// Aumento de honorarios por inflación - lazy
const AumentoInflacion = lazy(() => import('@/pages/AumentoInflacion'))

// Loading fallback para lazy components
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
)
import { usePermissions } from '@/hooks/usePermissions'

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth0()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

// Public route wrapper (redirects to dashboard if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth0()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

// Muestra el Copiloto solo a los emails habilitados. El resto vuelve al inicio, como en el menú.
function CopilotoRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth0()

  if (!isCopilotoEnabled(user)) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

// Role-protected route wrapper
function RoleProtectedRoute({
  children,
  requiredPermission,
}: {
  children: React.ReactNode
  requiredPermission: string
}) {
  const { puede, isLoading } = usePermissions()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  // Verificar permiso
  const tienePermiso = puede[requiredPermission as keyof typeof puede]

  if (!tienePermiso) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive">Acceso Denegado</h1>
          <p className="text-muted-foreground mt-2">
            No tienes permisos para acceder a esta seccion.
          </p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

export const router = createBrowserRouter(
  [
    {
      path: '/login',
      element: (
        <PublicRoute>
          <Login />
        </PublicRoute>
      ),
    },
    {
      path: '/',
      element: (
        <ProtectedRoute>
          <MainLayout />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: <Dashboard />,
        },
        {
          path: 'copiloto',
          element: (
            <CopilotoRoute>
              <Suspense fallback={<PageLoader />}>
                <CopilotoContable />
              </Suspense>
            </CopilotoRoute>
          ),
        },
        {
          path: 'clientes',
          element: (
            <RoleProtectedRoute requiredPermission="verClientes">
              <Clientes />
            </RoleProtectedRoute>
          ),
        },
        {
          path: 'movimientos',
          element: (
            <RoleProtectedRoute requiredPermission="verMovimientos">
              <Movimientos />
            </RoleProtectedRoute>
          ),
        },
        {
          path: 'presupuestos',
          element: (
            <RoleProtectedRoute requiredPermission="verPresupuestos">
              <Presupuestos />
            </RoleProtectedRoute>
          ),
        },
        {
          path: 'facturas',
          element: (
            <RoleProtectedRoute requiredPermission="verFacturas">
              <Facturas />
            </RoleProtectedRoute>
          ),
        },
        {
          path: 'presupuestos/nuevo',
          element: (
            <RoleProtectedRoute requiredPermission="crearPresupuesto">
              <PresupuestoNuevo />
            </RoleProtectedRoute>
          ),
        },
        {
          path: 'presupuestos/:id/editar',
          element: (
            <RoleProtectedRoute requiredPermission="editarPresupuesto">
              <PresupuestoEditar />
            </RoleProtectedRoute>
          ),
        },
        {
          path: 'presupuestos/:id',
          element: (
            <RoleProtectedRoute requiredPermission="verPresupuestos">
              <PresupuestoDetalle />
            </RoleProtectedRoute>
          ),
        },
        {
          path: 'documentos',
          element: (
            <RoleProtectedRoute requiredPermission="verDocumentos">
              <Documentos />
            </RoleProtectedRoute>
          ),
        },
        {
          path: 'impuestos',
          element: (
            <RoleProtectedRoute requiredPermission="verImpuestos">
              <Impuestos />
            </RoleProtectedRoute>
          ),
        },
        {
          path: 'cuentas',
          element: (
            <RoleProtectedRoute requiredPermission="verCuentas">
              <Cuentas />
            </RoleProtectedRoute>
          ),
        },
        {
          path: 'gastos',
          element: (
            <RoleProtectedRoute requiredPermission="verCuentas">
              <Gastos />
            </RoleProtectedRoute>
          ),
        },
        {
          path: 'servicios',
          element: (
            <RoleProtectedRoute requiredPermission="verClientes">
              <Servicios />
            </RoleProtectedRoute>
          ),
        },
        {
          path: 'servicios/aumento-inflacion',
          element: (
            <RoleProtectedRoute requiredPermission="editarHonorariosCliente">
              <Suspense fallback={<PageLoader />}>
                <AumentoInflacion />
              </Suspense>
            </RoleProtectedRoute>
          ),
        },
        {
          path: 'calendario',
          element: (
            <RoleProtectedRoute requiredPermission="verCalendario">
              <Calendario />
            </RoleProtectedRoute>
          ),
        },
        {
          path: 'tareas',
          element: (
            <RoleProtectedRoute requiredPermission="verTareas">
              <Tareas />
            </RoleProtectedRoute>
          ),
        },
        {
          path: 'reportes',
          element: (
            <RoleProtectedRoute requiredPermission="verReportes">
              <Reportes />
            </RoleProtectedRoute>
          ),
        },
        {
          path: 'notificaciones',
          element: <Notificaciones />,
        },
        {
          path: 'ayuda',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Ayuda />
            </Suspense>
          ),
        },
        {
          path: 'ayuda/:slug',
          element: (
            <Suspense fallback={<PageLoader />}>
              <AyudaArticulo />
            </Suspense>
          ),
        },
        {
          path: 'novedades',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Novedades />
            </Suspense>
          ),
        },
        {
          // ORR-169: sin guard de rol en el padre. El acceso a las secciones del
          // estudio (general/marca/fiscal/comunicaciones) lo controla Configuracion
          // (redirige si !canViewOrgSettings) + SettingsSidebar (filtra los links).
          // Así un asistente accede a su config personal (Mi Cuenta, Notificaciones)
          // sin ver la del estudio.
          path: 'configuracion',
          element: <Configuracion />,
          children: [
            { index: true, element: <Navigate to="general" replace /> },
            { path: 'general', element: <Suspense fallback={<PageLoader />}><GeneralSettings /></Suspense> },
            { path: 'marca', element: <Suspense fallback={<PageLoader />}><BrandSettings /></Suspense> },
            { path: 'fiscal', element: <Suspense fallback={<PageLoader />}><FiscalSettings /></Suspense> },
            { path: 'facturacion', element: <Suspense fallback={<PageLoader />}><FacturacionSettings /></Suspense> },
            { path: 'comunicaciones', element: <Suspense fallback={<PageLoader />}><CommunicationSettings /></Suspense> },
            { path: 'redes', element: <Suspense fallback={<PageLoader />}><SocialSettings /></Suspense> },
            { path: 'notificaciones', element: <Suspense fallback={<PageLoader />}><NotificationSettings /></Suspense> },
            { path: 'cuenta', element: <Suspense fallback={<PageLoader />}><AccountSettings /></Suspense> },
          ],
        },
        {
          path: 'usuarios',
          element: (
            <RoleProtectedRoute requiredPermission="gestionarUsuarios">
              <Usuarios />
            </RoleProtectedRoute>
          ),
        },
        {
          path: 'auditoria',
          element: (
            <RoleProtectedRoute requiredPermission="verAuditoria">
              <Auditoria />
            </RoleProtectedRoute>
          ),
        },
        // Admin: Sync de vencimientos fiscales (ORR-100)
        {
          path: 'admin/sync-vencimientos',
          element: (
            <RoleProtectedRoute requiredPermission="verSyncVencimientos">
              <Suspense fallback={<PageLoader />}>
                <SyncVencimientos />
              </Suspense>
            </RoleProtectedRoute>
          ),
        },
        // Ruta AFIP - Casillero Electronico
        {
          path: 'fiscal/casillero',
          element: <CasilleroElectronico />,
        },
      ],
    },
    // Rutas públicas (legales e informativas) - accesibles sin autenticación
    {
      path: '/terminos',
      element: (
        <Suspense fallback={<PageLoader />}>
          <TerminosServicio />
        </Suspense>
      ),
    },
    {
      path: '/privacidad',
      element: (
        <Suspense fallback={<PageLoader />}>
          <PoliticaPrivacidad />
        </Suspense>
      ),
    },
    {
      path: '/soporte',
      element: (
        <Suspense fallback={<PageLoader />}>
          <Soporte />
        </Suspense>
      ),
    },
    // Ruta pública para visualizar contratos de clientes
    {
      path: '/contrato/:token',
      element: (
        <Suspense fallback={<PageLoader />}>
          <ContratoCliente />
        </Suspense>
      ),
    },
  ],
  {
    basename: '/',  // Menta se sirve desde la raíz
  }
)
